import { useEffect, useMemo, useState } from "react";
import { ExpectingSupplierPaymentServices } from "../../services/expect-supplier-payment";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { debounce, formatDateFieldsFlat, formatNumberFixed, renderClassNameStatusEsp } from "../../utils/helper";
import { Button, Flex, Input, Modal, Space, Table } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faEye, faFilter, faPen, faPlus, faSearch, faTimeline } from "@fortawesome/free-solid-svg-icons";
import { IColumnConfig, ISortInfo } from "../../interfaces/common";
import CustomizeTableModal from "../../components/customize-table";
import { ColumnType, TableProps } from "antd/es/table";
import FilterDrawer from "../../components/filter-drawer";
import { TableViewFilters } from "../../interfaces/table-view";
import TableViewDropdown from "../../components/table-view-dropdown";
import { useTableViews } from "../../hooks/useTableViews";
import dayjs from "dayjs";
import { DATE_FORMAT, DATE_FORMAT_DDMMYYYY_HHMMSS } from "../../constants/common";
import { useUpdateTableView } from "../../hooks/useTableView";
import { countNonEmptyFields } from "../../utils/helper";
import { useDropdownsData } from "../../hooks/useDropdownsData";
import { IEspResponse, IEspResult } from "../../interfaces/expect-supplier-payment";
import BackButton from "../../components/back-button";
import { useNavigate, useSearchParams } from "react-router-dom";
import FilterBscOrEsp from "../booking-supplier-cost/components/filter";
import { usePageStore } from "../../context/PageStoreContext";
import CreateAndEditEsp from "./create-and-edit.esp";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permission";
import ModalChangeWorkflow from "./components/change-workflow";
import { useMessageApi } from "../../context/MessageContext";
import { ERROR_CODES, EspStatusEnum } from "../../constants/enum";
import ModalConfirm from "../../components/confirm";

const COL_FIXED_LEFT = ["supplierCode", "supplierName"];
const TABLE_NAME = "expected_supplier_payments";

const renderFieldTable = (fieldName: string, value: any) => {
  const dateFields = [
    "exportAt",
    "bookingDate",
    "departureDate",
    "returnDate",
    "componentStartDate",
    "componentEndDate",
    "dueDate",
  ];
  const numberFields = ["bookingPax", "numberOfNights", "supplierPax", "rooms"];
  const costFields = ["localCurrencyCost", "gbpCost"];
  const datetimeFields = ["exportedDatetime"];

  if (dateFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT) : value;
  }
  
  if (datetimeFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT_DDMMYYYY_HHMMSS) : value;
  }

  if (numberFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{value}</div>;
  }

  if (costFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{formatNumberFixed(value)}</div>;
  }

  if (fieldName === "pnr") {
    return value ? value?.split("/")?.join("\n") : value;
  }

  if (fieldName === "status") {
    return <span className={renderClassNameStatusEsp(value)}>{value}</span>;
  }

  return value;
};

const setWidthFieldTable = (fieldName: string) => {
  if (["supplierName"].includes(fieldName)) {
    return 300;
  }
  if (["supplierCode", "currency"].includes(fieldName)) {
    return 100;
  }
  if (["status"].includes(fieldName)) {
    return 120;
  }
  if (["pnr"].includes(fieldName)) {
    return 270;
  }
  if (["tourName"].includes(fieldName)) {
    return 200;
  }
  if (["leadPaxName"].includes(fieldName)) {
    return 145;
  }

  if (["localCurrencyCost", "gbpCost", "supplierPax", "rooms"].includes(fieldName)) {
    return 140;
  }
  if (["parentBookingRef"].includes(fieldName)) {
    return 150;
  }

  return 120;
};

const ExpectingSupplierPaymentPage: React.FC = () => {
  const messageApi = useMessageApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramsValue = searchParams.get("params");
  const supplierCodeEsp = paramsValue?.split("_")[0] || "";
  const bookingRefEsp = paramsValue?.split("_")[1] || "";
  const nameSupplier = searchParams.get("name");
  const [listEsp, setListEsp] = useState<IEspResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const [showModal, setShowModal] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [isModalCreateAndEdit, setIsModalCreateAndEdit] = useState(false);
  const [selectEsp, setSelectEsp] = useState<IEspResult>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showModalChangeWorkflow, setShowModalChangeWorkflow] = useState<boolean>(false);
  const [modalConfirm, setModalConfirm] = useState<any>(null);
  const [isFetchedMaterData, setIsFetchedMaterData] = useState(false);

  const { setPageValue, getPageValue } = usePageStore();
  const hasPermissionEditor = useHasPermission([PERMISSIONS.BOOKING_SUPPLIER_COST_EDITOR]);

  useEffect(() => {
    setSearchValue(getPageValue().searchValue);
    setDebouncedSearchValue(getPageValue().searchValue);
    setPagination((prev) => ({ ...prev, ...getPageValue().pagination }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    dataTableView,
    setDataTableView,
    loading: loadingTableView,
    refetchTableViews,
  } = useTableViews({
    tableName: TABLE_NAME,
  });

  const currentTableView = dataTableView?.currentView!;

  const { filters = {}, columns: columnConfigs = [] } = currentTableView || {};

  const isDefaultView = !!dataTableView?.defaultView;

  const { updateTableView } = useUpdateTableView();

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        keyword: debouncedSearchValue || undefined,
        ...formatDateFieldsFlat(filters), 
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };
      const response: IEspResponse = await ExpectingSupplierPaymentServices.getAllAndRow(paramsValue || "all", params);
      setListEsp(response.data.results);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
    } catch (error) {
      console.error("Fetch suppliers error:", error);
    } finally {
      setLoading(false);
      setIsFetchedMaterData(true);
    }
  };

  useEffect(() => {
    if (!loadingTableView) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTableView, debouncedSearchValue, filters, pagination.current, pagination.pageSize, sortInfo, paramsValue]);

  const { data: dropdownsData } = useDropdownsData({ serviceType: "espServices", enabled: isFetchedMaterData });

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
      }, 1000),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSaveCustomizeTable = async (configs: IColumnConfig[]) => {
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, columns: configs } });
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const handleApplyFilters = (values: TableViewFilters) => {
    const newFilters = { ...filters, ...values };
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, filters: newFilters } });
    if (isDefaultView) {
      updateTableView({
        tableName: TABLE_NAME,
        filters: newFilters,
      });
    }
    setDebouncedSearchValue("");
    setSearchValue("");
    setOpenFilter(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const colActive: ColumnType<IEspResult> = {
    title: "Actions",
    key: "action",
    width: 80,
    fixed: "right",
    align: "center",
    render: (_: any, record: IEspResult) => (
      <Space size="small" className="box-action" align="center">
        <Button type="link" size="small" onClick={() => handleCreateOrEditEsp(record)}>
          {hasPermissionEditor ? <FontAwesomeIcon icon={faPen} /> : <FontAwesomeIcon icon={faEye} />}
        </Button>
        <div>|</div>

        <Button type="link" size="small" onClick={() => linkTo(record.id)}>
          <FontAwesomeIcon icon={faTimeline} />
        </Button>
      </Space>
    ),
  };

  const columns: ColumnType<IEspResult>[] = useMemo(() => {
    return columnConfigs
      .filter((col) => col.isDisplay)
      .map((col) => {
        return {
          title: col.label,
          dataIndex: col.column,
          key: col.column,
          fixed: COL_FIXED_LEFT.includes(col.column) ? "left" : false,
          sorter: true,
          sortDirections: ["ascend", "descend"],
          render: (value: any) => renderFieldTable(col.column, value),
          ...(setWidthFieldTable(col.column) && { width: setWidthFieldTable(col.column) }),
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnConfigs]);

  const activeFilterCount = countNonEmptyFields(filters);

  const linkTo = (link: string) => {
    setPageValue({
      searchValue,
      pagination,
    });
    navigate(`/expect-supplier-payment/${link}/audit-trail`);
  };

  const handleCreateOrEditEsp = (esp?: IEspResult) => {
    setSelectEsp(esp);
    setIsModalCreateAndEdit(true);
  };

  const rowSelection: TableProps<any>["rowSelection"] = {
      selectedRowKeys,
      onChange: (newSelectedRowKeys: React.Key[], selectedRows: any[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
      },
      getCheckboxProps: (record: any) => ({
        id: record.id,
      }),
      preserveSelectedRowKeys: true,
  };
  
  const handleChangeWorkflow = async (espType?: string, isForce?: boolean) => {
    try {
      const response = await ExpectingSupplierPaymentServices.changeWorkflow({
        newType: espType,
        espIds: selectedRowKeys,
        isForce,
      });

      if (response.success) {
        messageApi.success("ESP Workflow Changed Successfully");
        setShowModalChangeWorkflow(false);
        setModalConfirm(null);
        setSelectedRowKeys([]);
        fetchData(); // Refresh data after change
        return;
      }

      // If not success but no error thrown, show generic error
      messageApi.error("Change ESP Workflow failed");
    } catch (error: any) {
      if (error?.errorCode === ERROR_CODES.ESP_FULL_ERROR) {
        setShowModalChangeWorkflow(false);
        setModalConfirm({
          isOpen: true,
          title: "Confirm Changing Workflow",
          description: "All ESPs of the BSC are not selected before changing. Do you still want to proceed?",
          onConfirm: () => handleChangeWorkflow(espType, true),
        });
      } else {
        messageApi.error(error?.message || "Change ESP Workflow failed");
      }
    }
  };

  return (
    <div className="esp-page">
      <BackButton label="Booking Supplier Cost" to="/booking-supplier-cost" />
      <h2 className="title-page">Expected Supplier Payment</h2>
      {paramsValue && (
        <Flex
          gap={24}
          justify="space-between"
          align="center"
          style={{ marginBottom: "16px", paddingBottom: "16px", borderBottom: "1px solid var(--text-black-100)" }}
        >
          <h4 style={{ margin: 0 }}>
            {supplierCodeEsp ?? ""} - {decodeURIComponent(nameSupplier ?? "")} - {bookingRefEsp ?? ""}
          </h4>
          <Button type="primary" onClick={() => handleCreateOrEditEsp()}>
            <FontAwesomeIcon icon={faPlus} /> Create
          </Button>
        </Flex>
      )}

      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Input
          value={searchValue}
          placeholder="Enter Booking Ref., Supp. Code, Supp. Name or Lead Pax Name"
          onChange={handleInputChange}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "var(--text-blue-500)" }} />}
          style={{ width: 600 }}
          size="large"
          allowClear
        />
        {selectedRowKeys?.length > 0 && (
          <Button
            type="primary"
            onClick={() => {
              setShowModalChangeWorkflow(true);
            }}
          >{`Select the new Workflow to move these ${selectedRowKeys.length} ESPs`}</Button>
        )}
        <Flex gap={12}>
          <Button color="primary" variant="outlined" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faBars} style={{ fontSize: "16px" }} />
            Customise
          </Button>
          <Button color="primary" variant="outlined" onClick={() => setOpenFilter(true)}>
            <FontAwesomeIcon icon={faFilter} style={{ fontSize: "16px" }} />
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
          <TableViewDropdown
            tableName={TABLE_NAME}
            dataTableView={dataTableView!}
            onTrigger={() => refetchTableViews()}
          />
        </Flex>
      </Flex>

      <Table
        dataSource={listEsp}
        loading={loading || loadingTableView}
        rowKey={(record) => record.id}
        rowSelection={{
          ...rowSelection,
          getCheckboxProps: (record) => ({
            disabled: [EspStatusEnum.MATCHED, EspStatusEnum.EXPORTED].includes(record.status),
          }),
        }}
        columns={[...columns, colActive]}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        size="small"
        className="table-fm"
        rowClassName={(record, index) => {
          let className = index % 2 === 0 ? "highlight-row" : "";
          return className.trim();
        }}
        scroll={{ x: 1200 }}
      />

      {showModal && (
        <CustomizeTableModal
          open={showModal}
          onClose={() => setShowModal(false)}
          configsValue={columnConfigs}
          onSave={handleSaveCustomizeTable}
          tableName={TABLE_NAME}
          isSave={isDefaultView}
        />
      )}
      {openFilter && (
        <FilterDrawer
          title="Filter Expected Supplier Payment"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          values={filters}
          onApply={handleApplyFilters}
          children={<FilterBscOrEsp dropdownsData={dropdownsData} type="ESP" tableName={TABLE_NAME} />}
        />
      )}
      {isModalCreateAndEdit && (
        <Modal
          open={isModalCreateAndEdit}
          onCancel={() => setIsModalCreateAndEdit(false)}
          footer={null}
          width={940}
          title={`${selectEsp ? "Update" : "Create new"} Expected Supplier Payment`}
        >
          <CreateAndEditEsp
            bookingRef={selectEsp?.bookingRef || bookingRefEsp}
            supplierCode={selectEsp?.supplierCode || supplierCodeEsp}
            espId={selectEsp?.id}
            onClose={(isTrigger) => {
              setIsModalCreateAndEdit(false);
              if (isTrigger) {
                fetchData();
              }
            }}
            isPermissionEdit={hasPermissionEditor}
          />
        </Modal>
      )}
      {showModalChangeWorkflow && (
        <ModalChangeWorkflow
          open={showModalChangeWorkflow}
          onCancel={() => setShowModalChangeWorkflow(false)}
          onChange={handleChangeWorkflow}
          espIds={selectedRowKeys}
        />
      )}
      {modalConfirm?.isOpen && (
        <ModalConfirm
          title={modalConfirm.title}
          description={modalConfirm.description}
          open={modalConfirm.isOpen}
          onCancel={() => setModalConfirm(null)}
          onOk={modalConfirm.onConfirm}
        />
      )}
    </div>
  );
};

export default ExpectingSupplierPaymentPage;
